import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as TenantsState from "../../store/Tenants";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { TenantResponse } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import SearchInput from "./SearchInput";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import { AdminTable } from "./AdminTable";

interface Injected {
  tenants: TenantsState.TenantsState;
}

type OwnProps = {
  onSelected?: (tenant: TenantResponse) => any;
  pageSize?: number;
  showFirst?: number | null | undefined;
  selectedTenants?: Array<TenantResponse>;
  renderButton?: (tenant: TenantResponse) => any;
  selectedLabel?: string;
  selectedBtnClass?: string;
  unselectedLabel?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar?: (node: React.ReactNode, options?: any) => void;
  closeSnackbar?: (key?: any) => void;
}

type Props = Injected &
  typeof TenantsState.actionCreators &
  OwnProps &
  SnackProps;

const SelectTenant: React.FC<Props> = (props) => {
  const [typedTenant, setTypedTenant] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { pageSize } = props;
      await props.setFetchParamsTenants({
        page: 0,
        pageSize: pageSize || defaultPageSize,
        searchTerm: typedTenant,
      });
    } catch (e) {
      console.error("Error requesting tenants", e);
      const message = (e as any)?.message || "Error requesting tenants";
      props.enqueueSnackbar &&
        props.enqueueSnackbar(<span style={{ fontSize: 12 }}>{message}</span>, {
          variant: "error",
        });
    }
  }, [props, typedTenant]);

  const searchTenant = useMemo(() => debounce(refresh, 1000), [refresh]);

  const handleTypedTenant = (value: string) => {
    setTypedTenant(value);
    searchTenant();
  };

  useEffect(() => {
    // initial load
    refresh();
    return () => {
      searchTenant.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // if pageSize change triggers refresh immediately

    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pageSize]);

  const defaultRenderButton = useCallback(
    (tenant: TenantResponse) => {
      const {
        selectedTenants,
        onSelected,
        btnStyle,
        selectedBtnClass,
        selectedLabel,
        unselectedBtnClass,
        unselectedLabel,
      } = props;
      const isSelected =
        selectedTenants && selectedTenants.find((s) => s.id === tenant.id);
      // className retained for backward compatibility if CSS depends on it
      const className = isSelected
        ? selectedBtnClass || "btn-primary"
        : unselectedBtnClass || "btn-default";
      return (
        <Button
          className={className}
          style={btnStyle as React.CSSProperties | undefined}
          onClick={() => onSelected && onSelected(tenant)}
          type="button"
          variant={isSelected ? "default" : "outline"}
        >
          {!isSelected
            ? unselectedLabel || "Select"
            : selectedLabel || "Unselect"}
        </Button>
      );
    },
    [props],
  );

  const renderTenantsTable = (tenantsData: TenantResponse[]) => {
    const { renderButton, showFirst } = props;
    const { isLoading } = props.tenants;
    let tenants = tenantsData;
    tenants = !showFirst ? tenants : (tenants || []).slice(0, showFirst);
    return (
      <div>
        <AdminTable aria-label="Tenants" containerClassName="mb-2">
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              <th className="w-[390px]" />
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant, i) => (
              <tr key={"f_" + i}>
                <td>{tenant.id}</td>
                <td>{tenant.name}</td>
                <td className="w-[390px]">
                  {renderButton
                    ? renderButton(tenant)
                    : defaultRenderButton(tenant)}
                </td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
        {!showFirst && (
          <PageControl
            onNext={props.nextTenantsPage}
            onPrev={props.prevTenantsPage}
            currentPage={props.tenants.page}
            isLoading={isLoading}
            disablePrev={props.tenants.page === 0}
            disableNext={props.tenants.tenants.length < props.tenants.pageSize}
          />
        )}
      </div>
    );
  };

  const { tenants, searchTerm, isLoading } = props.tenants;

  return (
    <div>
      <div className="flex justify-end ">
        <div className="w-[15vw]">
          <SearchInput
            name="typedTenant"
            placeholder="Search here"
            value={typedTenant}
            onChange={(e) => handleTypedTenant(e.target.value)}
          />
        </div>
      </div>
      <h4>{isLoading && <Spinner />}</h4>
      {renderTenantsTable(tenants)}
    </div>
  );
};

const ConnectedSelectTenant = connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    tenants: state.tenants,
  }),
  { ...TenantsState.actionCreators },
)(SelectTenant);

const SelectTenantWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectTenant
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectTenantWithSnackbar;
